/**
 * @fileoverview RowLevelExplorer — paginated, searchable grid for a single
 * category of an import run's classified rows.
 * @module import-editor/dryRunSummary
 *
 * Responsibilities:
 * - Fetch rows for the selected category from the `/rows` endpoint via the
 *   `fetchRows` prop.
 * - Provide paging (25 / 50 / 100 / 250), substring search (debounced), sort.
 * - Offer a one-click CSV export using the `onExportCsv` callback.
 * - Render coordinates with the short-in-cell + precise-tooltip convention.
 *
 * Dependencies:
 * - React (state + effects)
 * - formatCoordShort / formatCoordPrecise
 *
 * Architecture:
 * - [Local-first documented bypass] The rows data is NOT Dexie-cached.
 *   A 1M-row dataset would exceed browser IndexedDB limits. The server
 *   caches the classification for 10 minutes; that's the only cache layer.
 *   See plan §2 + §6.2.
 *
 * Notes:
 * - Presentation + local UI state only. ALL network access is delegated
 *   to `fetchRows` so the same component works in tenant-web and
 *   system-web with each app's own API client.
 */

/**
 * @component RowLevelExplorer
 *
 * Purpose:
 * - Drill-in view that sits inside an expanded CategoryAccordion.
 *
 * Props:
 * - runId:         import run ID (stable for the lifetime of the component)
 * - category:      which category is being explored (maps to /rows category param)
 * - fetchRows:     loader function returning a RowsPageResponse
 * - onExportCsv:   called when the user clicks Export CSV; parent builds URL
 * - title:         optional heading (defaults to category key)
 * - pageSizeOptions: defaults to [25, 50, 100, 250]
 *
 * Behavior:
 * - Re-fetches when page, pageSize, q (300ms debounced), sort, or category changes.
 * - Shows spinner while loading, banner on error.
 *
 * Data Source:
 * - remote (delegated via fetchRows prop)
 *
 * Constraints:
 * - MUST NOT fetch anything directly. Only via fetchRows.
 * - MUST NOT cache rows in Dexie. See file header.
 *
 * Performance:
 * - Debounce on search (300ms) to avoid spamming the endpoint.
 * - Requests are aborted when params change mid-flight (AbortController).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import type { FieldTransformEvidence } from '@firesquirrel/local-db';
import { formatCoordShort, formatCoordPrecise } from '../../utils/coordFormat';

export interface ClassifiedRowView {
  rowNumber: number;
  matchKeyValue: string;
  status: string;
  reason?: string;
  coordinates?: [number, number] | null;
  /**
   * Per-field transform provenance persisted on the run's row evidence.
   * Entries with `source: 'ai'` are AI-transform outputs; deterministic
   * entries describe non-AI transform results.
   */
  fieldTransforms?: FieldTransformEvidence[];
}

export interface RowsPageResponseView {
  runId: string;
  category: string;
  total: number;
  page: number;
  pageSize: number;
  rows: ClassifiedRowView[];
}

export interface FetchRowsParams {
  runId: string;
  category: string;
  page: number;
  pageSize: number;
  q: string;
  sort: 'rowNumber:asc' | 'rowNumber:desc';
  signal?: AbortSignal;
}

export interface RowLevelExplorerProps {
  runId: string;
  category: string;
  title?: string;
  pageSizeOptions?: number[];
  fetchRows: (params: FetchRowsParams) => Promise<RowsPageResponseView>;
  onExportCsv?: (params: { runId: string; category: string; q: string }) => void;
  /**
   * Test hook — disables the 300ms search debounce so tests can assert
   * behavior synchronously. Production callers should not set this.
   */
  disableDebounce?: boolean;
}

const DEFAULT_PAGE_SIZE_OPTIONS = [25, 50, 100, 250];
const SEARCH_DEBOUNCE_MS = 300;

export function RowLevelExplorer(props: RowLevelExplorerProps): JSX.Element {
  const { runId, category, fetchRows, onExportCsv } = props;
  const pageSizeOptions = props.pageSizeOptions ?? DEFAULT_PAGE_SIZE_OPTIONS;

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<number>(pageSizeOptions[1] ?? 50);
  const [sort, setSort] = useState<'rowNumber:asc' | 'rowNumber:desc'>('rowNumber:asc');
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');

  const [data, setData] = useState<RowsPageResponseView | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Debounce the search input so keypresses don't each fire a fetch.
  useEffect(() => {
    if (props.disableDebounce) {
      setQ(qInput);
      return;
    }
    const t = window.setTimeout(() => setQ(qInput), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [qInput, props.disableDebounce]);

  // Reset to page 0 whenever the filters change (search / category / pageSize).
  useEffect(() => { setPage(0); }, [q, category, pageSize, sort]);

  // Request cancellation: keep the latest AbortController so stale responses
  // don't clobber fresh data.
  const ctrlRef = useRef<AbortController | null>(null);

  useEffect(() => {
    ctrlRef.current?.abort();
    const ctrl = new AbortController();
    ctrlRef.current = ctrl;
    setLoading(true);
    setError(null);

    fetchRows({ runId, category, page, pageSize, q, sort, signal: ctrl.signal })
      .then((response) => {
        if (ctrl.signal.aborted) return;
        setData(response);
      })
      .catch((err: Error) => {
        if (ctrl.signal.aborted) return;
        setError(err.message ?? 'Failed to load rows');
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setLoading(false);
      });

    return () => { ctrl.abort(); };
  }, [fetchRows, runId, category, page, pageSize, q, sort]);

  const totalPages = useMemo(
    () => (data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1),
    [data],
  );

  const handleExport = (): void => {
    if (!onExportCsv) return;
    onExportCsv({ runId, category, q });
  };

  return (
    <div className="space-y-2">
      {/* Toolbar: search + page-size + export */}
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <input
          type="text"
          value={qInput}
          onChange={(e) => setQInput(e.target.value)}
          placeholder="Search match key or reason…"
          className="px-2 py-1 border border-gray-300 rounded-md flex-1 min-w-[200px] max-w-[320px]"
          aria-label={`Search rows in ${category}`}
        />
        <label className="flex items-center gap-1.5 text-gray-600">
          Rows per page:
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="px-1.5 py-1 border border-gray-300 rounded-md"
            aria-label="Rows per page"
          >
            {pageSizeOptions.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </label>
        <div className="ml-auto flex items-center gap-2">
          {onExportCsv && (
            <button
              type="button"
              onClick={handleExport}
              disabled={!data || data.total === 0}
              className="px-2.5 py-1 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Download the filtered rows as CSV"
            >
              Export CSV
            </button>
          )}
        </div>
      </div>

      {/* Status line */}
      <div className="flex items-center justify-between text-[11px] text-gray-500 px-1">
        <span aria-live="polite">
          {loading
            ? 'Loading…'
            : data
              ? `${data.total.toLocaleString()} row${data.total === 1 ? '' : 's'}`
              : '—'}
        </span>
        {data && data.total > data.pageSize && (
          <span>
            Page {data.page + 1} of {totalPages}
          </span>
        )}
      </div>

      {/* Error banner */}
      {error && (
        <div role="alert" className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-sm px-3 py-2">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="border border-gray-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto max-h-[420px]">
          <table className="min-w-full text-xs text-left">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-3 py-2 font-medium text-gray-700 w-16">
                  <button
                    type="button"
                    onClick={() => setSort((s) => (s === 'rowNumber:asc' ? 'rowNumber:desc' : 'rowNumber:asc'))}
                    className="inline-flex items-center gap-1 hover:text-gray-900"
                    aria-label="Sort by row number"
                  >
                    #
                    <span aria-hidden="true">{sort === 'rowNumber:asc' ? '↑' : '↓'}</span>
                  </button>
                </th>
                <th className="px-3 py-2 font-medium text-gray-700">Match Key</th>
                <th className="px-3 py-2 font-medium text-gray-700 w-40">Coordinates</th>
                <th className="px-3 py-2 font-medium text-gray-700">Transform outputs</th>
                <th className="px-3 py-2 font-medium text-gray-700">Reason</th>
              </tr>
            </thead>
            <tbody>
              {data && data.rows.length === 0 && !loading && (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-center text-gray-500">
                    {q ? 'No rows match your search.' : 'No rows in this category.'}
                  </td>
                </tr>
              )}
              {data?.rows.map((row, i) => (
                <tr key={`${row.rowNumber}-${i}`} className={i % 2 === 1 ? 'bg-gray-50/60 border-t' : 'border-t'}>
                  <td className="px-3 py-1.5 text-gray-500 tabular-nums">{row.rowNumber.toLocaleString()}</td>
                  <td className="px-3 py-1.5 font-mono text-gray-800 break-all">{row.matchKeyValue}</td>
                  <td
                    className="px-3 py-1.5 text-gray-600 tabular-nums"
                    title={row.coordinates ? formatCoordPrecise(row.coordinates) : ''}
                  >
                    {formatCoordShort(row.coordinates)}
                  </td>
                  <td className="px-3 py-1.5 text-gray-600">
                    {/* AI-produced transform outputs only — deterministic
                        provenance is omitted to keep the cell compact. */}
                    {(() => {
                      const aiOutputs = (row.fieldTransforms ?? []).filter((t) => t.source === 'ai');
                      if (aiOutputs.length === 0) return '—';
                      return (
                        <ul className="space-y-0.5">
                          {aiOutputs.map((t, j) => (
                            <li key={`${t.targetField}-${j}`} title={t.value} className="truncate">
                              <span className="font-mono text-gray-500">{t.targetField}</span>
                              {': '}
                              <span className="text-gray-800">{t.value}</span>
                            </li>
                          ))}
                        </ul>
                      );
                    })()}
                  </td>
                  <td className="px-3 py-1.5 text-gray-600">{row.reason ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {data && data.total > data.pageSize && (
        <div className="flex items-center justify-end gap-2 text-xs">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0 || loading}
            className="px-2 py-1 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            ‹ Previous
          </button>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1 || loading}
            className="px-2 py-1 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next ›
          </button>
        </div>
      )}
    </div>
  );
}

export default RowLevelExplorer;
